import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormHandles } from '@unform/core'
import { ValidationError } from 'yup'

import { FeedbackContext } from '~/components/feedback'
import { sanitizeAndFormatName } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  createAdSizesService,
  getAdSizesService
} from '~/modules/retailMedia/services/ad-size'

import { QUERY_KEY_GET_AD_SIZES } from '../../constants'
import { defaultVideoSizes, initialFormData } from '../constants'
import { adSizeSchema } from './validator'

const AdSizeContext = createContext<AdSizeProviderData<FormHandles>>(
  {} as AdSizeProviderData<FormHandles>
)

const AdSizeProvider = ({ children }: React.PropsWithChildren) => {
  const [formData, setFormData] = useState<AdSizeFormData>(initialFormData)

  const { toast } = useContext(FeedbackContext)
  const { tToasts } = useAPPTranslation()

  const formRef = useRef<FormHandles>(null)

  /**
   * Memos
   */

  /**
   * Prepare form data for onChange handling.
   * Removes leading zeros from dimensions and builds default name and label values.
   */
  const initialData = useMemo(() => {
    const removeInitialZero = (value: string) => value?.replace(/^0+/, '')

    const { width, height } = {
      width: removeInitialZero(formData.width),
      height: removeInitialZero(formData.height)
    }

    const defaultName = `${width}x${height}`
    const name = formData?.suggestedSize?.value || defaultName
    const label = formData?.suggestedSize?.label || defaultName

    return {
      width,
      height,
      name,
      label,
      type: formData.type
    }
  }, [formData])

  /**
   * Helpers
   */

  /** Handle name – remove special characters and replace spaces with underscores */
  const handleChangeName = useCallback(
    (data: InputOnChangeData) => {
      const callback = (value: string) =>
        formRef?.current?.setFieldValue('name', value)

      sanitizeAndFormatName(data.value, callback)
    },
    [formRef]
  )

  /**
   * Handles
   */

  /**
   * Handle media type change.
   * When the type is changed, the form interface will be adjusted.
   * If the selected type is "video", we will display only a select dropdown with predefined video resolutions.
   */
  const handleChangeType = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      setFormData(prevState => ({
        ...prevState,
        type: value as AdSizeType,
        suggestedSize: null
      }))
    },
    []
  )

  /**
   * Handle default size usage.
   * Since predefined values are not editable, the selected option
   * will be used to populate the width and height fields.
   */
  const handleSuggestedSizes = useCallback((value: AdSizeOption) => {
    setFormData(prevState => ({
      ...prevState,
      suggestedSize: value,
      width: value?.width,
      height: value?.height,
      name: value?.value,
      label: value?.label
    }))
  }, [])

  /**
   * Handle input change.
   * Updates the ID and name fields based on changes in the form.
   */
  const handleChangeSizes = useCallback((inputData: OnChangeInputData) => {
    const { name, value } = inputData

    setFormData(prevState => ({ ...prevState, [name]: value }))
  }, [])

  /**
   * Handle submit
   */

  const queryClient = useQueryClient()

  const { isPending, mutateAsync } = useMutation({
    mutationFn: createAdSizesService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GET_AD_SIZES
      })

      tToasts({
        prefix: 'rm:adsize.create.messages.success',
        toastFunction: toast.success
      })
    },
    onError: () => {
      tToasts({
        prefix: 'rm:adsize.create.messages.error',
        toastFunction: toast.critical
      })
    }
  })

  const formatPayload = (formData: FormDataAdSize): RequestBodyAdSize => {
    const { suggestedSize, ...rest } = formData

    if (suggestedSize) {
      const sizeData = defaultVideoSizes.find(
        item => item.value === suggestedSize
      )

      if (sizeData) {
        return {
          ...rest,
          width: sizeData.width,
          height: sizeData.height,
          name: sizeData.value,
          label: sizeData.label
        }
      }
    }

    if (rest.type === 'digital_signage') {
      const name = `${rest.width}x${rest.height}`
      return { ...rest, name, label: name }
    }

    return rest
  }

  const validatePayload = async (input: RequestBodyAdSize) => {
    await adSizeSchema.validate(input, { abortEarly: false })
  }

  const handleSubmit = useCallback(
    async (formData: FormDataAdSize) => {
      const body = formatPayload(formData)

      try {
        await validatePayload(body)
        await mutateAsync(body)
        setFormData(initialFormData)
        formRef.current?.reset()
      } catch (error) {
        if (error instanceof ValidationError) {
          tToasts({
            prefix: 'rm:adsize.create.messages.error',
            toastFunction: toast.critical
          })
          console.error('🚀 ~ error:', error)
        }
      }
    },
    [mutateAsync, tToasts, toast]
  )

  /**
   * Helper memos
   */

  /** Check if the size already exists */
  const { data: sizesToCompare } = useQuery({
    queryKey: QUERY_KEY_GET_AD_SIZES,
    queryFn: async () => {
      const res = await getAdSizesService()
      console.log('🚀 ~ res:', res)
      return res.data?.data
    }
  })

  const sizeExists = useMemo(
    () =>
      sizesToCompare?.some(
        item =>
          item.height === formData.height &&
          item.width === formData.width &&
          item.type === formData.type
      ),
    [sizesToCompare, formData.height, formData.type, formData.width]
  )

  /** Check if ID fields should be displayed */
  const showIdsFields = useMemo(
    () =>
      (formData.type === 'image' ||
        formData.type === 'sponsored_brand_image' ||
        formData.type === 'sponsored_brand_video') &&
      formData.width &&
      formData.height &&
      !sizeExists,
    [formData.type, formData.width, formData.height, sizeExists]
  )

  /** Check if form is ready to submit */
  const readyToSubmit = useMemo(
    () => Boolean(!sizeExists && formData.width && formData.height),
    [formData.width, formData.height, sizeExists]
  )

  /**
   * Filter suggested sizes by sizes already created
   */

  const suggestedSizes = useMemo(() => {
    if (!sizesToCompare) return defaultVideoSizes

    return defaultVideoSizes.filter(
      size =>
        !sizesToCompare.some(
          item =>
            item.width === size.width &&
            item.height === size.height &&
            item.type === size.type
        )
    )
  }, [sizesToCompare])

  return (
    <AdSizeContext.Provider
      value={{
        formRef,

        formData,
        showIdsFields,
        readyToSubmit,
        sizeExists,
        isPending,

        initialData,
        suggestedSizes,

        handleChangeName,

        handleChangeType,
        handleSuggestedSizes,
        handleChangeSizes,
        handleSubmit
      }}
    >
      {children}
    </AdSizeContext.Provider>
  )
}

function useAdSizeForm(): AdSizeProviderData<FormHandles> {
  const context = useContext(AdSizeContext)

  if (!context) {
    throw new Error('useAdSizeForm must be used within an AdSizeProvider')
  }

  return context
}

export { AdSizeContext, AdSizeProvider, useAdSizeForm }
